import { useUserStore } from '@itc/sdk';
import type { Pinia } from 'pinia';
import type { RouteRecordRaw } from 'vue-router';
import { modules } from '../modules.ts';
import { firstAdminPath } from './sections.ts';

/** /admin and its sections. Each section's route requires its own permission. */
export function adminRoutes(pinia: Pinia): RouteRecordRaw {
  return {
    path: '/admin',
    component: () => import('./AdminLayout.vue'),
    meta: { permission: 'authenticated', title: 'Admin' },
    children: [
      {
        path: '',
        // The first section the user may open; none means they may not open the panel at all.
        redirect: () => {
          const user = useUserStore(pinia);
          return firstAdminPath((permission) => user.can(permission)) ?? { path: '/forbidden', query: { from: '/admin' } };
        },
      },
      { path: 'users', component: () => import('./users/UserSearch.vue'), meta: { permission: 'core.admin.users', title: 'Users' } },
      { path: 'users/:id', component: () => import('./users/UserProfile.vue'), meta: { permission: 'core.admin.users', title: 'User' } },
      { path: 'groups', component: () => import('./groups/GroupList.vue'), meta: { permission: 'core.admin.groups', title: 'Groups' } },
      { path: 'groups/:id', component: () => import('./groups/GroupEdit.vue'), meta: { permission: 'core.admin.groups', title: 'Group' } },
      { path: 'role-links', component: () => import('./role-links/RoleLinkList.vue'), meta: { permission: 'core.admin.roles', title: 'Role links' } },
      { path: 'role-links/:id', component: () => import('./role-links/RoleLinkDetail.vue'), meta: { permission: 'core.admin.roles', title: 'Role link' } },
      { path: 'permissions', component: () => import('./permissions/PermissionsPage.vue'), meta: { permission: 'core.admin.groups', title: 'Permissions' } },
      { path: 'audit', component: () => import('./audit/AuditLog.vue'), meta: { permission: 'core.audit.view', title: 'Audit log' } },
      { path: 'system', component: () => import('./system/SystemPage.vue'), meta: { permission: 'core.system.view', title: 'System' } },
      {
        path: 'settings/:module',
        component: () => import('./settings/ModuleSettings.vue'),
        // Gated per module (its <name>.admin, or core.admin.settings for core) by the guard below; the server checks too.
        meta: { permission: 'authenticated', title: 'Settings' },
        beforeEnter: (to) => {
          const user = useUserStore(pinia);
          const module = String(to.params['module']);
          return user.can(module === 'core' ? 'core.admin.settings' : `${module}.admin`) ? true : { path: '/forbidden', query: { from: to.fullPath } };
        },
      },
      ...modules.flatMap((module) =>
        (module.admin ?? []).map((section) => ({ path: section.path.slice('/admin/'.length), component: section.component, meta: { permission: section.permission, title: section.label } })),
      ),
    ],
  };
}
